import { Routes, Route, Navigate, useParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext.jsx'
import SettingsLayout from '../components/SettingsLayout.jsx'
import { TILE } from '../lib/tileLook.js'
import Overview from './Overview.jsx'
import { People, JobTypes, ScheduleSettings, NotifySettings } from './AdminSections.jsx'
import { ModeHistory, CatSetup, Displays, Branding, Events, Data } from './OwnerSections.jsx'
import { Configure } from './ConfigureSection.jsx'
import { AssetManagement } from './AssetSections.jsx'
import { ListsSettings } from './ListsSections.jsx'
import {
  IntegrationsHome, IntegrationDetail, ManageIntegrations,
} from './IntegrationsSections.jsx'

// One settings surface for everyone. The sidebar groups sections by what they are *for* —
// people, scheduling, the company's own setup — rather than by the role that unlocks them,
// because somebody hunting for a setting knows what they want to change, not which of their
// roles happens to gate it. "Owner" was a label about permissions, and it put CAT setup and
// Branding side by side purely because the same role opens both.
//
// Access is therefore applied per item, and a group left with nothing in it is dropped rather
// than rendered empty. Every gate is repeated as a hard guard on the route itself, so a typed
// URL cannot reach a section the sidebar didn't offer.

/** A route whose section was hidden from this role never renders — only a redirect does. */
function Guard({ ok, children }) {
  return ok ? children : <Navigate to="/settings/overview" replace />
}

/** The detail pane for whichever integration the URL names. */
function IntegrationRoute() {
  const { key } = useParams()
  return <IntegrationDetail integrationKey={key} />
}

export default function Settings() {
  const { can, modeName, modeEnabled, assetsName, listsEnabled } = useAuth()
  // A mode the company has turned off takes its setup pane with it — there is nothing to
  // configure for a board nobody can be switched onto. The route guards below repeat it, so
  // a typed URL cannot reach a pane the sidebar withheld.
  const groupOn = modeEnabled('cat')
  const teamOn = modeEnabled('normal')
  // Same rule for lists: a company that has them off has no page of them.
  const listsOn = listsEnabled()

  // Each row spreads one entry of TILE (lib/tileLook.js), which carries the pair of colours
  // its glyph tile is drawn in. The colour is the point: the icon SHAPE alone is a weak thing
  // to aim at down a list of fourteen, and colour is what lets somebody hit "Job types" from
  // muscle memory without reading a word. Spread rather than copied because a mode wears the
  // same colour here and on its card in Configure, and two hex codes with a comment asking
  // the next person to keep them in step is not a way to hold that.
  const sections = [
    {
      key: 'general',
      label: 'General',
      items: [
        { path: '/settings/overview', label: 'Overview', ...TILE.overview, show: true },
        // About moved to Help → About, beside the guide and What's new: "which build am I
        // on" is asked while looking for help, and its commit date is what What's new is
        // dated by. The route below keeps old links and bookmarks landing.
      ],
    },
    {
      key: 'people',
      label: 'People',
      items: [
        { path: '/settings/user-management', label: 'User Management', ...TILE.people, show: can.administer },
      ],
    },
    {
      key: 'scheduling',
      label: 'Scheduling',
      items: [
        // PM teams renders as part of this page now (see ScheduleSettings in
        // AdminSections.jsx), rather than behind its own nav item.
        { path: '/settings/team-setup', label: `${modeName('normal')} Mode Setup`, ...TILE.modeNormal, show: can.manageTeamMode && teamOn },
        { path: '/settings/group-setup', label: `${modeName('cat')} Mode Setup`, ...TILE.modeCat, show: can.manageGroupMode && groupOn },
        { path: '/settings/job-types', label: 'Job types', ...TILE.jobTypes, show: can.administer },
        // Shown to everyone once the company has lists, like the asset register: what lists
        // exist is not a secret from the people on them, and the page itself withholds the
        // editing controls from anyone the server says cannot use them.
        { path: '/settings/lists', label: 'Lists', ...TILE.lists, show: listsOn },
        { path: '/settings/tenant-mode-history', label: 'Mode history', ...TILE.modeHistory, show: can.isOwner },
      ],
    },
    {
      key: 'assets',
      label: assetsName(),
      items: [
        // Shown to everyone signed in, unlike every other section on this page. The register
        // is read-only for most of them — "which van is ours" is not a secret from the people
        // who drive it, and a list nobody can see is a list nobody keeps up to date. Named
        // with the company's own word for it (Settings → Configure).
        { path: '/settings/assets', label: `${assetsName()} Management`, ...TILE.assets, show: true },
      ],
    },
    {
      key: 'company',
      label: 'Company',
      items: [
        { path: '/settings/configure-tenant', label: 'Configure Tenant', ...TILE.configure, show: can.isOwner },
        { path: '/settings/branding', label: 'Branding', ...TILE.branding, show: can.isOwner },
        { path: '/settings/notifications', label: 'Notifications', ...TILE.notifications, show: can.administer },
        { path: '/settings/displays', label: 'Wall displays', ...TILE.displays, show: can.isOwner },
        // Beside wall displays, which is the other "something outside is talking to us" row.
        // Shown to everyone, whether or not anything is switched on: an integration that is
        // off is drawn dimmed on the page rather than hidden, so somebody who goes looking
        // learns it exists and who to ask. `nested` keeps the row highlighted while a detail
        // or the manage pane is open beneath it.
        { path: '/settings/integrations', label: 'Integrations', ...TILE.integrations, show: true, nested: true },
        { path: '/settings/logs', label: 'Audit log', ...TILE.auditLog, show: can.isOwner },
        // Below the audit log: the two are the company's record — what happened, and a copy
        // of everything as it stands.
        { path: '/settings/data', label: 'Data', ...TILE.data, show: can.isOwner },
      ],
    },
  ]
    .map((s) => ({ ...s, items: s.items.filter((i) => i.show) }))
    .filter((s) => s.items.length > 0)

  return (
    <SettingsLayout title="Settings" sections={sections}>
      <Routes>
        {/* Overview is the landing pane for every role — see Overview.jsx for why the mode
            switch lives there rather than on a page of its own. */}
        {/* Absolute, not "overview": since React Router 7 a relative path inside a splat
            route resolves against the whole URL, so the catch-all below sent
            /settings/nonsense to /settings/nonsense/overview — which it caught again, forever.
            tests/settings-routes.test.jsx opens exactly that address. */}
        <Route index element={<Navigate to="/settings/overview" replace />} />
        <Route path="overview" element={<Overview />} />
        <Route path="about" element={<Navigate to="/help/about" replace />} />

        <Route path="user-management" element={<Guard ok={can.administer}><People /></Guard>} />
        <Route path="job-types" element={<Guard ok={can.administer}><JobTypes /></Guard>} />
        {/* No guard: reading the register is open to anyone signed in, and the page itself
            withholds the editing controls from anyone the server says cannot use them. */}
        <Route path="assets" element={<AssetManagement />} />
        <Route path="team-setup" element={<Guard ok={can.manageTeamMode && teamOn}><ScheduleSettings /></Guard>} />
        <Route path="lists" element={<Guard ok={listsOn}><ListsSettings /></Guard>} />

        {/* The grid is open to everyone; the owner's panel is not, and the guard repeats
            what the button's absence already implies. A detail pane checks for itself that
            the integration it names is real and switched on. */}
        <Route path="integrations" element={<IntegrationsHome />} />
        <Route path="integrations/manage" element={<Guard ok={can.isOwner}><ManageIntegrations /></Guard>} />
        <Route path="integrations/:key" element={<IntegrationRoute />} />
        <Route path="notifications" element={<Guard ok={can.administer}><NotifySettings /></Guard>} />

        <Route path="tenant-mode-history" element={<Guard ok={can.isOwner}><ModeHistory /></Guard>} />
        <Route path="group-setup" element={<Guard ok={can.manageGroupMode && groupOn}><CatSetup /></Guard>} />
        <Route path="displays" element={<Guard ok={can.isOwner}><Displays /></Guard>} />
        <Route path="configure-tenant" element={<Guard ok={can.isOwner}><Configure /></Guard>} />
        <Route path="branding" element={<Guard ok={can.isOwner}><Branding /></Guard>} />
        <Route path="logs" element={<Guard ok={can.isOwner}><Events /></Guard>} />
        <Route path="data" element={<Guard ok={can.isOwner}><Data /></Guard>} />

        <Route path="*" element={<Navigate to="/settings/overview" replace />} />
      </Routes>
    </SettingsLayout>
  )
}
